<script setup lang="ts">
import { useNavigationData } from '~~/src/composables/useNavigationData'

interface Props {
  isOpen: boolean
}

defineProps<Props>()

defineOptions({
  name: 'SidebarLeftElementalHomeLinks',
})

const route = useRoute()
const { getNavigationItems } = useNavigationData()
const navigationItems = getNavigationItems()

function isActiveRoute(href: string) {
  return route.path === href
}
</script>

<template>
  <div class="sidebar-menu">
    <span class="sidebar-menu-label">Homes</span>

    <ul class="menu-items">
      <li
        v-for="item in navigationItems"
        :key="item.href"
        class="menu-item"
      >
        <a
          :href="item.href"
          class="sidebar-menu-button"
          :class="{ 'is-active': isActiveRoute(item.href) }"
          :data-active="isActiveRoute(item.href)"
          :title="item.title"
          @click.stop
        >
          <component
            :is="item.icon"
            :size="24"
            class="shrink-0"
            :class="isActiveRoute(item.href) ? 'text-foreground' : 'text-muted-foreground'"
          />
          <span
            v-if="isOpen"
            class="menu-label"
          >{{ item.title }}</span>
        </a>
      </li>
    </ul>
  </div>
</template>

<style lang="scss" scoped>
.sidebar-menu {
  @apply
    flex
    w-full
    min-w-0
    flex-col;
}

.sidebar-menu-label {
  @apply
    flex
    h-8
    shrink-0
    items-center
    rounded-md
    px-2
    text-xs
    font-medium
    text-gray-500
    outline-none
    transition-[margin,opacity]
    duration-200
    ease-linear
    sr-only;
}

.menu-items {
  @apply
    flex
    w-full
    min-w-0
    flex-col;

  gap: 24px;
}

.menu-item {
  @apply
    relative;
}

.sidebar-menu-button {
  @apply
    flex
    items-center
    justify-center
    overflow-hidden
    rounded-full
    outline-none
    cursor-pointer
    hover:bg-gray-100
    active:bg-gray-100
    transition-[width,background-color]
    duration-200
    ease-linear;

  width: 48px;
  height: 48px;
  padding: 12px;

  // When sidebar is open, expand to full width
  .sidebar-left.is-open & {
    @apply w-full
      justify-start;
  }

  &.is-active {
    @apply
      bg-gray-100
      font-medium;
  }

  svg {
    @apply
      shrink-0
      transition-colors
      duration-200
      w-6
      h-6;
  }
}

.menu-label {
  @apply
    font-bold
    text-[16px]
    truncate
    m-2;
}

// Screen reader only
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border-width: 0;
}
</style>
